import React, { useState, useEffect, useMemo, memo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import './Banner.css';
import SignUpForm from '../SignUpForm/index';
import { useGoogleTranslate } from '../GoogleTranslate';
import UserWayAccessibilityMenu from "../UserWayAccessibilityMenu/index";

// Separate text content from the main component
const content = {
  en: {
    heading: "The Way to \nYour Own Home",
    subheading: "A Free Online Class to Help You Buy with Confidence",
    description: "Buying your first home is a big deal—but it doesn't have to be confusing or stressful.",
    joinText: "Join Joaquin Claudio, an experienced Mortgage Loan Originator, for a free, no-pressure webinar designed to give you the knowledge and tools you need to buy your first home confidently."
  },
  es: {
    heading: "El Camino A \nTu Propio Hogar",
    subheading: "Una Clase Online Gratuita para Ayudarte a Comprar con Confianza",
    description: "Comprar tu primera hogar es un gran asunto, pero no tiene por qué ser confuso o estresante.",
    joinText: "Únete a Joaquin Claudio, un Originador de Préstamos Hipotecarios experimentado, para un seminario web gratuito y sin presión diseñado para darte el conocimiento y las herramientas que necesitas para comprar tu primer hogar con confianza."
  }
};

// Memoized text component for the join text (LCP element)
const JoinText = memo(({ text }) => (
  <p className="banner-join-text notranslate" translate="no">
    {text}
  </p>
));

// Memoized banner logo component
const BannerLogo = memo(({ src, alt }) => (
  <div className="banner-logo-container">
    <img 
      src={src} 
      alt={alt} 
      className="banner-image"
      width="128"
      height="85" 
      loading="eager"
      fetchpriority="high"
    />
  </div>
));

const HomeBanner = ({ Lodata, defaultLanguage }) => {
  const { getCurrentLanguage } = useGoogleTranslate();
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage || 'en');
  const imgCDNURL = process.env.REACT_APP_CDN_PATH_THEWAYTOYOUROWNHOME + "assets/";
  // Listen for language changes - reduced frequency and only after initial render
  useEffect(() => {
    const checkLanguage = () => {
      const lang = getCurrentLanguage();
      if (lang !== currentLanguage) {
        setCurrentLanguage(lang);
      }
    };
    
    // Initial check
    checkLanguage();
    
    // Reduced polling frequency and delayed start
    let intervalId;
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(checkLanguage, 5000); // Check every 5 seconds instead of every 1 second
    }, 3000); // Start checking 3 seconds after component mount
    
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [getCurrentLanguage, currentLanguage, defaultLanguage]);
  
  // Memoize content selection to prevent unnecessary recalculations
  const selectedContent = useMemo(() => 
    content[currentLanguage] || content.en
  , [currentLanguage]);

  // Memoize logo path generation to prevent unnecessary string concatenations
  const logoPath = useMemo(() => {
    const logoKey = currentLanguage === 'es' ? 'spanish' : 'english';
    return `${process.env.REACT_APP_CDN_PATH_THEWAYTOYOUROWNHOME}${Lodata?.banner?.logos?.[logoKey] || 
      (currentLanguage === 'es' ? 'assets/your-own-home-spanish.webp' : 'assets/your-own-home.webp')}`;
  }, [currentLanguage, Lodata?.banner?.logos]);
  
  // Extract content variables for readability
  const { heading, subheading, description, joinText } = selectedContent;
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 991.9);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth > 991.9);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
<div className="home-banner">
  <div className="videoBackground">
    <video autoPlay muted loop playsInline className="backgroundVideo">
      <source src={isDesktop ? `${imgCDNURL}banner-bg-video-desktop.mp4` : `${imgCDNURL}banner-bg-video-mobile.mp4`} loading="eager" type="video/mp4" />
    </video>

    {/* All your banner content including text, form etc should be inside videoBackground */}
    <Container className='banner-container'>
      <div className="mobile-AccessibilityMenu-show">
        <UserWayAccessibilityMenu color="white" width={30} height={30} />
      </div>
      <Row>
        <Col lg={7} className="banner-content">
          <h1 className="banner-heading notranslate" translate="no">
            {heading.split('\n').map((line, i) => (
              i === 0 ? line : <React.Fragment key={i}><br />{line}</React.Fragment>
            ))}
          </h1>

          <BannerLogo src={logoPath} alt="Banner Image" />

          <h2 className="banner-subheading notranslate" translate="no">{subheading}</h2>
          <p className="banner-description notranslate" translate="no">
            {description}
          </p>

          <JoinText text={joinText} />
        </Col>
        <Col lg={5} className="registration-form">
          <div className="form-container" id="form-section">
            <SignUpForm Lodata={Lodata} />
          </div>
        </Col>
      </Row>
    </Container>
  </div>
</div>

  );
};

export default HomeBanner;